import React, { useContext, useEffect, useState } from 'react'
import { columnsServices, getOpCurrentStageLabel, getOpStatusText } from '../utils'
import trash from '../../../../assets/productions/trash.svg'
import search from '../../../../assets/productions/search-in.svg'
import pen from '../../../../assets/productions/pen.svg'
import qr_code from '../../../../assets/productions/qr_code.svg'
import ModalContext from '../../../../contexts/ModalContext'
import axios from '../../../../config/axios'
import { ServicesContext, ServicesObjectType } from '../../../../contexts/ServicesContext'
import { QRCodeContext } from '../../../../contexts/QRCodeContext'
import { TdButtons, GlobalTable, HeaderLine, Td, Th, Tr } from '../../../../components/styles/table-style'
import LoadMoreButton from '../../../../components/LoadMoreButton'
import { TableContainer } from '../style'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { ProductionOutletType } from '..'
import { TypeMainOutlet } from '../..'
import { Filter } from '../../../../components/SubHeaderFilter/types'
import OrdenoLoading from '../../../../components/OrdenoLoading'

interface PaginationResponse {
  data: ServicesObjectType[];
  total: number;
  page: number;
  limit: number;
}

const ServicesTable: React.FC = () => {
  const { setModalSection, setModalActive } = useContext(ModalContext)
  const { setService } = useContext(ServicesContext)
  const { setContentJson } = useContext(QRCodeContext)

  const [servicesList, setServicesList] = useState<ServicesObjectType[]>([])
  const [totalServices, setTotalServices] = useState<number>(0)
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [hasNextPage, setHasNextPage] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const { setTabIndex, search, appliedFilters } = useOutletContext<ProductionOutletType>()
  const navigate = useNavigate()

  const ITEMS_PER_PAGE = 50

  useEffect(() => {
    setTabIndex(1)
  }, [setTabIndex])

  const fetchServices = async (page: number = 1, searchTerm: string = '', filters: Filter[] = [], append: boolean = false) => {
    setIsLoading(true)
    setError(null)

    try {
      const params: any = {
        page,
        limit: ITEMS_PER_PAGE
      }

      if (searchTerm && searchTerm.trim() !== '') {
        params.search = searchTerm.trim()
      }

      if (filters.length > 0) {
        const filterObj: any = {}
        filters.forEach(filter => {
          if (filter.value !== undefined && filter.value !== null && String(filter.value).trim() !== '') {
            filterObj[filter.key] = {
              value: filter.value,
              filterType: filter.filterType || 'Contém'
            }
          }
        })

        if (Object.keys(filterObj).length > 0) {
          params.filters = JSON.stringify(filterObj)
        }
      }

      const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/pcp/services/paginated`, {
        params
      })

      const data = response.data
      const returnedItems = data.data || []
      
      console.log('👀 [DEBUG] Itens recebidos da tabela de Produção:', returnedItems);
      
      const returnedCount = returnedItems.length
      const total = data.total || 0

      if (append) {
        setServicesList(prev => {
          const existingIds = new Set(prev.map(s => s.id));
          const newServices = returnedItems.filter((s: any) => !existingIds.has(s.id));
          const updatedServices = [...prev, ...newServices];
          const currentTotal = updatedServices.length;
        const hasMore = returnedCount === ITEMS_PER_PAGE && currentTotal < total;
        setHasNextPage(hasMore);
        console.log('➡️ [SERVICES] Append - hasMore:', hasMore, 'returnedCount:', returnedCount, 'currentTotal:', currentTotal, 'total:', total);
          return updatedServices;
        });
      } else {
      setServicesList(returnedItems);
      const currentTotal = returnedItems.length;
      const hasMore = returnedCount === ITEMS_PER_PAGE && currentTotal < total;
      setHasNextPage(hasMore);
      console.log('🆕 [SERVICES] First load - hasMore:', hasMore, 'returnedCount:', returnedCount, 'currentTotal:', currentTotal, 'total:', total);
      }

      setTotalServices(total)
      setCurrentPage(page)

    } catch (err: any) {
      if (!append) {
        setServicesList([])
        setTotalServices(0)
        setHasNextPage(false)
      }
    } finally {
      setIsLoading(false)
    }
  }

  const [debouncedSearch, setDebouncedSearch] = useState<string>('')
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(search || '')
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [search])

  useEffect(() => {
    setCurrentPage(1)
    setServicesList([])
    fetchServices(1, debouncedSearch, appliedFilters || [], false)
  }, [debouncedSearch, appliedFilters])

  const handleLoadMore = () => {
    if (!isLoading && hasNextPage) {
      const nextPage = currentPage + 1
      fetchServices(nextPage, debouncedSearch, appliedFilters || [], true)
    }
  }

  const handleClickModal = (section: string, service: any) => {
    setService(service)
    setModalActive(true)
    setModalSection(section)
  }

  const handleDelete = async (id: number) => {
    try {
      await axios.delete(`${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/service/${id}`)

      setCurrentPage(1)
      setServicesList([])
      fetchServices(1, debouncedSearch, appliedFilters || [], false)
    } catch (error) {
      setError('Erro ao deletar serviço')
    }
  }

  if (isLoading && servicesList.length === 0) {
    return (
      <TableContainer>
        <OrdenoLoading variant="inline" text="Carregando serviços..." style={{ padding: '4rem 0' }} />
      </TableContainer>
    )
  }

  if (error && servicesList.length === 0) {
    return (
      <TableContainer>
        <div style={{ textAlign: 'center', padding: '2rem', color: 'red' }}>
          Erro ao carregar serviços: {error}
          <button
            onClick={() => fetchServices(1, debouncedSearch, appliedFilters || [], false)}
            style={{ marginLeft: '1rem', padding: '0.5rem 1rem', cursor: 'pointer' }}
          >
            Tentar novamente
          </button>
        </div>
      </TableContainer>
    )
  }

  return (
    <TableContainer>
      <GlobalTable>
        <thead>
          <HeaderLine>
            {columnsServices.map((item, index) => (
              <Th key={index}>{item}</Th>
            ))}
          </HeaderLine>
        </thead>
        <tbody>
          {servicesList.length > 0 ? (
            servicesList.map((item: any) => (
              <Tr key={item.id} onClick={() => navigate(`card/?id=${item.id}`)}>
                <Td>{item.codeOfOP || 'N/A'}</Td>
                <Td>{item.stockInProduction?.length || 0}</Td>
                <Td>
                  {item.stockInProduction?.map((stockItem: any) =>
                    stockItem.stock?.description
                  ).filter(Boolean).join(' | ') || 'Sem materiais'}
                </Td>
                <Td>
                  {item.orderItem?.order?.client?.companyName?.split(' ').slice(0, 2).join(' ') || 'Cliente não informado'}
                </Td>
                <Td>{getOpCurrentStageLabel(item.service_stage || [])}</Td>
                <Td>{getOpStatusText(item.status)}</Td>
              </Tr>
            ))
          ) : (
            <Tr>
              <td colSpan={columnsServices.length} style={{ textAlign: 'center', padding: '2rem' }}>
                {debouncedSearch || (appliedFilters && appliedFilters.length > 0) ?
                  'Nenhum serviço encontrado para os filtros aplicados.' :
                  'Nenhum serviço encontrado.'
                }
              </td>
            </Tr>
          )}
        </tbody>
      </GlobalTable>
      <LoadMoreButton
        onLoadMore={handleLoadMore}
        isLoading={isLoading}
        hasMore={hasNextPage}
        currentCount={servicesList.length}
        totalCount={totalServices}
      />
    </TableContainer>
  )
}

export default ServicesTable